import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, getDocs, deleteDoc, serverTimestamp, getDoc, orderBy } from 'firebase/firestore';
import { PlusCircle, Edit, Trash2, CheckCircle, XCircle, Eye, Loader2, CalendarDays, Printer } from 'lucide-react';

// --- COMPOSANT RÉUTILISABLE: SELECT AVEC AJOUT DYNAMIQUE ---
const SelectWithAdd = ({ label, options, selectedValues, onUpdate, optionType }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newValue, setNewValue] = useState('');

    const handleAdd = () => {
        const trimmedValue = newValue.trim();
        if (trimmedValue && !options.includes(trimmedValue)) {
            const newValues = [...selectedValues, trimmedValue];
            onUpdate(optionType, newValues);
            setNewValue('');
            setIsAdding(false);
        }
    };
    
    const handleSelectChange = (e) => {
        const value = e.target.value;
        if (value === "add_new") {
            setIsAdding(true);
            return;
        }

        const isSelected = selectedValues.includes(value);
        let newSelection = isSelected 
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onUpdate(optionType, newSelection);
    };

    return (
        <div className="space-y-2 p-3 border border-gray-200 rounded-lg bg-white">
            <label className="block text-sm font-bold text-gray-700">{label}</label>
            
            <div className="flex flex-wrap gap-2 mb-2 min-h-[30px]">
                {selectedValues.map(v => (
                    <span key={v} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center">
                        {v}
                        <button 
                            type="button" 
                            onClick={() => onUpdate(optionType, selectedValues.filter(val => val !== v))}
                            className="ml-2 text-blue-600 hover:text-blue-900 font-bold"
                        >
                            &times;
                        </button>
                    </span>
                ))}
                {selectedValues.length === 0 && <p className="text-sm italic text-gray-500">Aucun(e) sélectionné(e).</p>}
            </div>

            <select 
                onChange={handleSelectChange}
                value="" 
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 text-gray-700 focus:ring-blue-500 focus:border-blue-500"
            >
                <option value="" disabled>Sélectionner un standard ou Ajouter...</option>
                {options.map(opt => (
                    <option key={opt} value={opt} disabled={selectedValues.includes(opt)}>
                        {opt} {selectedValues.includes(opt) ? '(Déjà ajouté)' : ''}
                    </option>
                ))}
                <option value="add_new" className="font-bold text-green-600">-- Ajouter un nouvel élément --</option>
            </select>

            {isAdding && (
                <div className="flex space-x-2 mt-2">
                    <input
                        type="text"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder={`Nouvelle valeur pour ${label}...`}
                        className="flex-grow border border-gray-300 rounded-md shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                    >
                        Ajouter
                    </button>
                    <button
                        type="button"
                        onClick={() => {setIsAdding(false); setNewValue('');}}
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition"
                    >
                        Annuler
                    </button>
                </div>
            )}
        </div>
    );
};

const CoursManagement = ({ setMessage, setError, db, schoolId, userRole, userId }) => {
  const [selectedWeekStartDate, setSelectedWeekStartDate] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [ficheTitle, setFicheTitle] = useState('');
  const [competencesVisees, setCompetencesVisees] = useState([]);
  const [methodesDominantes, setMethodesDominantes] = useState([]);
  const [objectifsSpecifiques, setObjectifsSpecifiques] = useState('');
  const [deroulementContent, setDeroulementContent] = useState('');
  const [differenciationContent, setDifferenciationContent] = useState('');
  const [evaluationContent, setEvaluationContent] = useState('');
  
  const [standardOptions, setStandardOptions] = useState({
    competences: [
        "Maîtriser l'expression écrite pour argumenter (Référentiel)",
        "Résoudre des problèmes complexes (Analyse)",
        "Identifier et utiliser les figures géométriques",
        "Communiquer oralement avec aisance"
    ],
    methodes: [
        "Démarche inductive (découverte)",
        "Pédagogie par projet (ABP)",
        "Cours dialogué et magistral",
        "Classe inversée (Flipped classroom)"
    ]
  });
  
  const [currentView, setCurrentView] = useState('list');
  const [editingFiche, setEditingFiche] = useState(null);
  const [viewingFiche, setViewingFiche] = useState(null);

  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teacherName, setTeacherName] = useState('Chargement...');

  const [pendingFiches, setPendingFiches] = useState([]);
  const [validatedFiches, setValidatedFiches] = useState([]);
  const [rejectedFiches, setRejectedFiches] = useState([]);

  const [rejectionReason, setRejectionReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [currentTeacherId, setCurrentTeacherId] = useState(null);
  const [classMatieresAssignments, setClassMatieresAssignments] = useState([]);

  const handleUpdateStructuredData = useCallback((type, newValues) => {
    switch (type) {
        case 'competences':
            setCompetencesVisees(newValues);
            newValues.forEach(val => {
                if (!standardOptions.competences.includes(val)) {
                    setStandardOptions(prev => ({ ...prev, competences: [...prev.competences, val] }));
                }
            });
            break;
        case 'methodes':
            setMethodesDominantes(newValues);
            newValues.forEach(val => {
                if (!standardOptions.methodes.includes(val)) {
                    setStandardOptions(prev => ({ ...prev, methodes: [...prev.methodes, val] }));
                }
            });
            break;
        default:
            break;
    }
  }, [standardOptions.competences, standardOptions.methodes]);

  // --- RÉCUPÉRATION DU PROFIL ENSEIGNANT AVEC SES CLASSES ET MATIÈRES ---
  const getTeacherProfile = useCallback(async (teacherAuthUid) => {
    if (!teacherAuthUid || !schoolId || !db) return null;
    
    try {
      // Recherche de l'enseignant par userId (UID d'authentification)
      const q = query(
        collection(db, 'schools', schoolId, 'enseignants'),
        where('userId', '==', teacherAuthUid)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const teacherDoc = querySnapshot.docs[0];
        const data = teacherDoc.data();
        
        // Récupérer le nom
        const fullName = `${data.prenom || ''} ${data.nom || ''}`.trim() || 'Nom Inconnu';
        
        return {
          id: teacherDoc.id,
          authUid: teacherAuthUid,
          name: fullName,
          rawData: data
        };
      } else {
        // Si pas trouvé dans enseignants, chercher dans users
        const userDocRef = doc(db, 'users', teacherAuthUid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const fullName = userData.displayName || userData.email || 'Utilisateur Inconnu';
          
          return {
            id: teacherAuthUid, // Utiliser l'UID comme ID
            authUid: teacherAuthUid,
            name: fullName,
            rawData: userData
          };
        }
      }
    } catch (error) {
      console.error("Error fetching teacher profile:", error);
    }
    
    return null;
  }, [db, schoolId]);

  // --- RÉCUPÉRATION DU NOM DE L'ENSEIGNANT (pour l'affichage) ---
  const getTeacherName = useCallback(async (teacherAuthUid) => { 
    if (!teacherAuthUid || !schoolId || !db) return 'Nom Inconnu';
    
    try {
      const q = query(
        collection(db, 'schools', schoolId, 'enseignants'),
        where('userId', '==', teacherAuthUid)
      );
      const querySnapshot = await getDocs(q); 

      if (!querySnapshot.empty) {
        const teacherDoc = querySnapshot.docs[0];
        const data = teacherDoc.data();
        return `${data.prenom || ''} ${data.nom || ''}`.trim() || 'Nom Inconnu';
      }
    } catch (error) {
      console.error("Error fetching teacher name:", error);
    }
    
    return 'Nom Inconnu';
  }, [db, schoolId]);

  // --- RÉCUPÉRATION DU NOM D'UN UTILISATEUR PAR SON UID (pour le validateur) ---
  const getUserDisplayNameByUid = useCallback(async (uid) => {
    if (!uid || !db) return 'Utilisateur Inconnu';
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        return data.displayName || data.email || 'Utilisateur Inconnu';
      }
    } catch (error) {
      console.error("Error fetching user display name:", error);
    }
    return 'Utilisateur Inconnu';
  }, [db]);
  
  // --- RÉCUPÉRATION DES INFOS DE L'ÉCOLE ---
  const getSchoolInfo = useCallback(async () => {
    let schoolInfo = { name: 'Nom de l\'École', address: '', phone: '' };
    try {
      if (schoolId) {
        const schoolRef = doc(db, 'schools', schoolId);
        const schoolSnap = await getDoc(schoolRef);
        if (schoolSnap.exists()) {
          const data = schoolSnap.data();
          schoolInfo = {
            name: data.name || schoolInfo.name, 
            address: data.adresse_ecole || schoolInfo.address,
            phone: data.telephone_ecole || schoolInfo.phone,
          };
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des infos de l'école pour l'impression:", error);
    }
    return schoolInfo;
  }, [db, schoolId]);

  // --- RÉCUPÉRATION DU NOM D'UN ENSEIGNANT PAR SON ID DOCUMENT (pour les admin) ---
  const getTeacherNameById = useCallback(async (teacherDocId) => {
    if (!teacherDocId || !schoolId || !db) return 'Nom Inconnu';
    
    try {
      const teacherDocRef = doc(db, 'schools', schoolId, 'enseignants', teacherDocId);
      const teacherDocSnap = await getDoc(teacherDocRef);
      
      if (teacherDocSnap.exists()) {
        const data = teacherDocSnap.data();
        return `${data.prenom || ''} ${data.nom || ''}`.trim() || 'Nom Inconnu';
      }
    } catch (error) {
      console.error("Error fetching teacher by ID:", error);
    }
    
    return 'Nom Inconnu';
  }, [db, schoolId]);

  // --- CHARGEMENT DES DONNÉES AVEC FILTRES POUR ENSEIGNANTS ---
  useEffect(() => {
    if (!db) {
      setError('Base de données Firestore non disponible.');
      setIsLoading(false);
      return;
    }

    setError('');
    setMessage('');

    let unsubscribeSubjects, unsubscribeClasses, unsubscribeFiches, unsubscribeClassMatieres;

    const initializeData = async () => {
      if (schoolId) {
        // Fonction pour charger les matières assignées à un enseignant
        const loadTeacherSubjects = async (teacherId) => {
          try {
            // 1. Chercher dans class_matieres
            const q1 = query(
              collection(db, 'schools', schoolId, 'class_matieres'),
              where('assignedTeacherIds', 'array-contains', teacherId)
            );
            const snapshot1 = await getDocs(q1);
            const subjectIdsFromClassMatieres = snapshot1.docs.map(doc => doc.data().matiereId);
            
            // 2. Chercher dans matieres_enseignees (si cette collection existe)
            try {
              const q2 = query(
                collection(db, 'schools', schoolId, 'matieres_enseignees'),
                where('enseignantId', '==', teacherId)
              );
              const snapshot2 = await getDocs(q2);
              const subjectIdsFromMatieresEnseignees = snapshot2.docs.map(doc => doc.data().matiereId);
              
              // Fusionner les deux listes
              return [...new Set([...subjectIdsFromClassMatieres, ...subjectIdsFromMatieresEnseignees])];
            } catch (e) {
              // Si la collection n'existe pas, utiliser seulement class_matieres
              return subjectIdsFromClassMatieres;
            }
          } catch (error) {
            console.error("Error loading teacher subjects:", error);
            return [];
          }
        };

        // Fonction pour charger les classes assignées à un enseignant
        const loadTeacherClasses = async (teacherId) => {
          try {
            // 1. Chercher dans teacherClassAssignments
            const q1 = query(
              collection(db, 'schools', schoolId, 'teacherClassAssignments'),
              where('teacherId', '==', teacherId)
            );
            const snapshot1 = await getDocs(q1);
            const classIdsFromAssignments = snapshot1.docs.map(doc => doc.data().classId);
            
            // 2. Chercher dans class_matieres (classes où l'enseignant a des matières)
            const q2 = query(
              collection(db, 'schools', schoolId, 'class_matieres'),
              where('assignedTeacherIds', 'array-contains', teacherId)
            );
            const snapshot2 = await getDocs(q2);
            const classIdsFromClassMatieres = snapshot2.docs.map(doc => doc.data().classId);
            
            // Fusionner les deux listes
            return [...new Set([...classIdsFromAssignments, ...classIdsFromClassMatieres])];
          } catch (error) {
            console.error("Error loading teacher classes:", error);
            return [];
          }
        };

        // Fonction pour charger les affectations classe-matière
        const loadClassMatieresAssignments = async () => {
          try {
            const q = query(collection(db, 'schools', schoolId, 'class_matieres'));
            const snapshot = await getDocs(q);
            const assignments = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setClassMatieresAssignments(assignments);
          } catch (error) {
            console.error("Error loading class-matieres assignments:", error);
          }
        };

        if (userRole === 'enseignant' && userId) {
          // 1. Charger le profil enseignant
          const teacherProfile = await getTeacherProfile(userId);
          
          if (teacherProfile) {
            setTeacherName(teacherProfile.name);
            setCurrentTeacherId(teacherProfile.id);
            
            // 2. Charger les matières assignées à cet enseignant
            const assignedSubjectIds = await loadTeacherSubjects(teacherProfile.id);
            setTeacherSubjects(assignedSubjectIds);
            
            // 3. Charger les classes assignées à cet enseignant
            const assignedClassIds = await loadTeacherClasses(teacherProfile.id);
            setTeacherClasses(assignedClassIds);
            
            // 4. Charger les affectations classe-matière
            await loadClassMatieresAssignments();
            
            // 5. Charger toutes les matières globales (puis filtrer)
            const allSubjectsRef = collection(db, 'schools', schoolId, 'matieres_globales');
            unsubscribeSubjects = onSnapshot(
              allSubjectsRef,
              (snapshot) => {
                const allSubjects = snapshot.docs.map(doc => ({ 
                  id: doc.id, 
                  name: doc.data().name,
                  description: doc.data().description,
                  niveau: doc.data().niveau,
                  type: doc.data().type,
                  status: doc.data().status || 'actif',
                  ...doc.data() 
                }));
                
                // Filtrer les matières assignées à cet enseignant ET actives
                const filteredSubjects = allSubjects.filter(subject => 
                  assignedSubjectIds.includes(subject.id) && 
                  subject.status === 'actif'
                );
                
                setSubjects(filteredSubjects);
              },
              (err) => { 
                console.error("Erreur lors du chargement des matières:", err);
                setError("Erreur lors du chargement des matières."); 
              }
            );

            // 6. Charger toutes les classes (puis filtrer)
            const allClassesRef = collection(db, 'schools', schoolId, 'classes');
            unsubscribeClasses = onSnapshot(
              allClassesRef,
              (snapshot) => {
                const allClasses = snapshot.docs.map(doc => ({ 
                  id: doc.id, 
                  nom: doc.data().nom,
                  niveau: doc.data().niveau,
                  serie: doc.data().serie,
                  ...doc.data() 
                }));
                
                // Filtrer les classes assignées à cet enseignant
                const filteredClasses = allClasses.filter(cls => 
                  assignedClassIds.includes(cls.id)
                );
                
                setClasses(filteredClasses);
              },
              (err) => { 
                console.error("Erreur lors du chargement des classes:", err);
                setError("Erreur lors du chargement des classes."); 
              }
            );

            // 7. Charger les fiches de cet enseignant
            const fichesCollectionRef = collection(db, 'schools', schoolId, 'weeklyCourses');
            const fichesQuery = query(fichesCollectionRef, where('teacherId', '==', teacherProfile.id));
            
            unsubscribeFiches = onSnapshot(
              fichesQuery,
              (snapshot) => {
                const fichesData = snapshot.docs.map(doc => ({ 
                  id: doc.id, 
                  ...doc.data() 
                }));
                
                // Trier les fiches par date décroissante
                const sortedFiches = fichesData.sort((a, b) => {
                  const dateA = a.weekStartDate?.toDate() || new Date(0);
                  const dateB = b.weekStartDate?.toDate() || new Date(0);
                  return dateB - dateA;
                });
                
                setPendingFiches(sortedFiches.filter(f => f.status === 'pending'));
                setValidatedFiches(sortedFiches.filter(f => f.status === 'validated'));
                setRejectedFiches(sortedFiches.filter(f => f.status === 'rejected'));
                setIsLoading(false);
              },
              (err) => { 
                console.error("Erreur lors du chargement des fiches:", err);
                setError("Erreur lors du chargement des fiches hebdomadaires."); 
                setIsLoading(false); 
              }
            );
          } else {
            setError("Profil enseignant non trouvé ou non configuré. Veuillez vérifier vos affectations.");
            setIsLoading(false);
          }
        } 
        // Pour les rôles admin/directeur : tout voir
        else if (userRole === 'directeurPedagogique' || userRole === 'admin' || userRole === 'superAdmin') {
          // Charger toutes les matières globales ACTIVES
          unsubscribeSubjects = onSnapshot(
            query(collection(db, 'schools', schoolId, 'matieres_globales'), where('status', '==', 'actif')),
            (snapshot) => {
              setSubjects(snapshot.docs.map(doc => ({ 
                id: doc.id, 
                name: doc.data().name,
                description: doc.data().description,
                niveau: doc.data().niveau,
                type: doc.data().type,
                ...doc.data() 
              })));
            },
            (err) => { 
              console.error("Erreur lors du chargement des matières:", err);
              setError("Erreur lors du chargement des matières."); 
            }
          );

          // Charger toutes les classes
          unsubscribeClasses = onSnapshot(
            collection(db, 'schools', schoolId, 'classes'),
            (snapshot) => {
              setClasses(snapshot.docs.map(doc => ({ 
                id: doc.id, 
                nom: doc.data().nom,
                niveau: doc.data().niveau,
                serie: doc.data().serie,
                ...doc.data() 
              })));
            },
            (err) => { 
              console.error("Erreur lors du chargement des classes:", err);
              setError("Erreur lors du chargement des classes."); 
            }
          );

          // Charger les affectations classe-matière pour les admin aussi
          loadClassMatieresAssignments();

          // Charger toutes les fiches avec tri par date
          const fichesCollectionRef = collection(db, 'schools', schoolId, 'weeklyCourses');
          const fichesQuery = query(fichesCollectionRef, orderBy('weekStartDate', 'desc'));
          
          unsubscribeFiches = onSnapshot(
            fichesQuery,
            (snapshot) => {
              const fichesData = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
              }));
              
              setPendingFiches(fichesData.filter(f => f.status === 'pending'));
              setValidatedFiches(fichesData.filter(f => f.status === 'validated'));
              setRejectedFiches(fichesData.filter(f => f.status === 'rejected'));
              setIsLoading(false);
            },
            (err) => { 
              console.error("Erreur lors du chargement des fiches:", err);
              setError("Erreur lors du chargement des fiches hebdomadaires."); 
              setIsLoading(false); 
            }
          );
          
          // Charger aussi le nom de l'utilisateur courant si c'est un directeur/admin
          if (userId) {
            const name = await getTeacherName(userId);
            setTeacherName(name);
          }
        } else {
          setError("Vous n'avez pas les permissions nécessaires pour voir les fiches.");
          setIsLoading(false);
        }
      } else {
        setSubjects([]);
        setClasses([]);
        setPendingFiches([]);
        setValidatedFiches([]);
        setRejectedFiches([]);
        setMessage("Veuillez sélectionner une école.");
        setIsLoading(false);
      }
    };

    initializeData();
    return () => {
      if (unsubscribeSubjects) unsubscribeSubjects();
      if (unsubscribeClasses) unsubscribeClasses();
      if (unsubscribeFiches) unsubscribeFiches();
      if (unsubscribeClassMatieres) unsubscribeClassMatieres();
    };
  }, [db, schoolId, userRole, userId, setMessage, setError, getTeacherProfile, getTeacherName]);

  const clearForm = useCallback(() => {
    setFicheTitle('');
    setSelectedWeekStartDate('');
    setSelectedSubject('');
    setSelectedClass('');
    setCompetencesVisees([]);
    setMethodesDominantes([]);
    setObjectifsSpecifiques('');
    setDeroulementContent('');
    setDifferenciationContent('');
    setEvaluationContent('');
    setEditingFiche(null);
  }, []);

  const handleEditFiche = (fiche) => {
    setEditingFiche(fiche);
    setFicheTitle(fiche.title);
    const dateString = fiche.weekStartDate?.toDate().toISOString().split('T')[0] || '';
    setSelectedWeekStartDate(dateString); 
    setSelectedSubject(fiche.subjectId);
    setSelectedClass(fiche.classId);
    setCompetencesVisees(fiche.competencesVisees || []);
    setMethodesDominantes(fiche.methodesDominantes || []);
    setObjectifsSpecifiques(fiche.objectifsSpecifiques || '');
    setDeroulementContent(fiche.deroulementContent || fiche.content || '');
    setDifferenciationContent(fiche.differenciationContent || '');
    setEvaluationContent(fiche.evaluationContent || '');
    setCurrentView('create');
  };
  
  const handleViewFiche = (fiche) => {
    setViewingFiche(fiche);
    setCurrentView('view');
  };

  const handleDeleteFiche = async (ficheId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette fiche ?')) {
      try {
        await deleteDoc(doc(db, 'schools', schoolId, 'weeklyCourses', ficheId));
        setMessage('Fiche supprimée avec succès !');
      } catch (e) {
        console.error("Error deleting fiche:", e);
        setError(`Erreur lors de la suppression de la fiche : ${e.message}`);
      }
    }
  };

  // --- VÉRIFIER SI UN ENSEIGNANT EST ASSIGNÉ À UNE MATIÈRE POUR UNE CLASSE ---
  const checkTeacherAssignment = useCallback((teacherId, classId, subjectId) => {
    // Si c'est un admin/directeur, toujours autoriser
    if (userRole !== 'enseignant') {
      return true;
    }
    
    // Vérifier dans les affectations classe-matière
    const assignment = classMatieresAssignments.find(
      a => a.classId === classId && a.matiereId === subjectId
    );
    
    if (!assignment) {
      console.warn(`Aucune affectation trouvée pour classe ${classId}, matière ${subjectId}`);
      return false;
    }
    
    const assignedTeachers = assignment.assignedTeacherIds || [];
    return assignedTeachers.includes(teacherId);
  }, [classMatieresAssignments, userRole]);

  // --- SOUMISSION ET MISE À JOUR DE LA FICHE ---
  const handleSubmitFiche = async (e) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!selectedWeekStartDate || !selectedSubject || !selectedClass || !ficheTitle || 
        competencesVisees.length === 0 || methodesDominantes.length === 0 || !deroulementContent.trim()) {
      setError('Veuillez remplir au moins la Date, la Matière, la Classe, le Titre, sélectionner les Compétences, les Méthodes, et détailler le Déroulement.');
      return;
    }

    // Pour les enseignants : vérification des assignations
    if (userRole === 'enseignant' && currentTeacherId) {
      // Vérifier si la matière est assignée à l'enseignant
      if (!teacherSubjects.includes(selectedSubject)) {
        setError('Cette matière ne vous est pas assignée. Veuillez sélectionner une matière de votre périmètre d\'enseignement.');
        return;
      }
      
      // Vérifier si la classe est assignée à l'enseignant
      if (!teacherClasses.includes(selectedClass)) {
        setError('Cette classe ne vous est pas assignée. Veuillez sélectionner une classe de votre périmètre d\'enseignement.');
        return;
      }
      
      // Vérifier si l'enseignant est bien assigné à cette matière pour cette classe
      const isAssigned = checkTeacherAssignment(currentTeacherId, selectedClass, selectedSubject);
      if (!isAssigned) {
        setError('Vous n\'êtes pas assigné à cette matière pour cette classe. Veuillez vérifier vos affectations.');
        return;
      }
    }

    setMessage('');
    setError('');

    try {
      // Récupérer le profil complet de l'enseignant
      let teacherProfile;
      let teacherIdToUse;
      let teacherNameToUse;
      
      if (userRole === 'enseignant') {
        teacherProfile = await getTeacherProfile(userId);
        if (!teacherProfile) {
          setError("Impossible de récupérer votre profil enseignant.");
          return;
        }
        teacherIdToUse = teacherProfile.id;
        teacherNameToUse = teacherProfile.name;
      } else {
        // Pour les admin/directeurs, on récupère juste le nom
        const name = await getTeacherName(userId);
        teacherIdToUse = userId;
        teacherNameToUse = name;
      }

      const subjectObj = subjects.find(s => s.id === selectedSubject);
      const classObj = classes.find(c => c.id === selectedClass);

      if (!subjectObj || !classObj) {
        setError("Matière ou classe introuvable. Veuillez réessayer.");
        return;
      }

      const subjectName = subjectObj.name;
      const className = classObj.nom;
      
      const weekDate = new Date(selectedWeekStartDate);
      const combinedContent = `
**Fiche de Préparation : ${ficheTitle}**
---
**Professeur:** ${teacherNameToUse}
**Matière/Classe:** ${subjectName} / ${className}
**Semaine du:** ${weekDate.toLocaleDateString('fr-FR')}
---
**Compétence(s) Visée(s):**
${competencesVisees.map(c => `* ${c}`).join('\n')}
---
**Méthode(s) Pédagogique(s) Dominante(s):**
${methodesDominantes.map(m => `* ${m}`).join('\n')}
---
**Objectifs Spécifiques:**
${objectifsSpecifiques.trim()}
---
**Déroulement Détaillé:**
${deroulementContent.trim()}
---
**Différenciation/Remédiation:**
${differenciationContent.trim()}
---
**Évaluation:**
${evaluationContent.trim()}
`;
      
      const ficheData = {
        schoolId: schoolId,
        title: ficheTitle,
        teacherId: teacherIdToUse,
        teacherAuthUid: userId,
        teacherName: teacherNameToUse,
        subjectId: selectedSubject,
        subjectName: subjectName,
        classId: selectedClass,
        className: className,
        weekStartDate: weekDate, 
        competencesVisees: competencesVisees,
        methodesDominantes: methodesDominantes,
        objectifsSpecifiques: objectifsSpecifiques.trim(),
        deroulementContent: deroulementContent.trim(),
        differenciationContent: differenciationContent.trim(),
        evaluationContent: evaluationContent.trim(),
        content: combinedContent.trim(), 
        status: 'pending', 
        submittedAt: serverTimestamp(),
      };

      if (editingFiche) {
        const updateData = {
          ...ficheData,
          status: editingFiche.status === 'rejected' ? 'pending' : editingFiche.status,
          validatedBy: null,
          validatorDisplayName: null,
          validatedAt: null,
          rejectionReason: null,
        };
        await updateDoc(doc(db, 'schools', schoolId, 'weeklyCourses', editingFiche.id), updateData);
        setMessage('Fiche hebdomadaire mise à jour avec succès !');
      } else {
        await addDoc(collection(db, 'schools', schoolId, 'weeklyCourses'), ficheData);
        setMessage('Fiche hebdomadaire soumise pour validation !');
      }

      clearForm();
      setCurrentView('list');
    } catch (e) {
      console.error("Error submitting fiche:", e);
      setError(`Erreur lors de la soumission de la fiche : ${e.message}`);
    }
  };

  const handleValidateFiche = async (ficheId) => {
    if (!userId) { setError("Erreur : ID de l'utilisateur validateur manquant."); return; }
    try {
      const validatorDisplayName = await getUserDisplayNameByUid(userId);
      await updateDoc(doc(db, 'schools', schoolId, 'weeklyCourses', ficheId), {
        status: 'validated',
        validatedBy: userId,
        validatorDisplayName: validatorDisplayName,
        validatedAt: serverTimestamp(),
        rejectionReason: null 
      });
      setMessage('Fiche validée avec succès !');
      setViewingFiche(null);
    } catch (e) {
      console.error("Error validating fiche:", e);
      setError(`Erreur lors de la validation de la fiche : ${e.message}`);
    }
  };

  const handleRejectFiche = async (ficheId) => {
    if (!rejectionReason.trim()) { setError("Veuillez fournir une raison de rejet."); return; }
    if (!userId) { setError("Erreur : ID de l'utilisateur rejetant manquant."); return; }
    try {
      const validatorDisplayName = await getUserDisplayNameByUid(userId);
      await updateDoc(doc(db, 'schools', schoolId, 'weeklyCourses', ficheId), {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
        validatedBy: userId, 
        validatorDisplayName: validatorDisplayName,
        validatedAt: serverTimestamp(), 
      });
      setMessage('Fiche rejetée avec succès.');
      setViewingFiche(null);
      setRejectionReason('');
    } catch (e) {
      console.error("Error rejecting fiche:", e);
      setError(`Erreur lors du rejet de la fiche : ${e.message}`);
    }
  };

  const handlePrintFiche = async () => {
    if (!viewingFiche) {
      setError("Aucune fiche sélectionnée pour l'impression.");
      return;
    }

    const schoolInfo = await getSchoolInfo();
    const contentHtml = viewingFiche.content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');

    const html = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Fiche de Cours - ${viewingFiche.title}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #007bff; padding-bottom: 10px; }
          .header h1 { margin: 0; font-size: 24px; color: #007bff; }
          .header p { margin: 2px 0; font-size: 12px; }
          .title { text-align: center; margin-bottom: 25px; font-size: 20px; font-weight: bold; color: #333; }
          .info-box { display: flex; flex-wrap: wrap; margin-bottom: 20px; border: 1px solid #ccc; padding: 10px; border-radius: 5px; background-color: #f8f9fa; }
          .info-box p { flex: 1 1 50%; margin: 5px 0; font-size: 14px; }
          .content-section { margin-bottom: 20px; padding: 15px; border: 1px solid #eee; border-radius: 5px; }
          .content-section strong { display: block; margin-top: 15px; margin-bottom: 5px; font-size: 16px; color: #007bff; border-bottom: 1px dashed #ccc; padding-bottom: 5px;}
          .content-body { white-space: pre-wrap; font-size: 14px; }
          .footer { text-align: right; margin-top: 30px; font-size: 10px; }
          @media print {
            .info-box p { flex: 1 1 50%; }
            .content-section { border: none; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${schoolInfo.name}</h1>
          <p>${schoolInfo.address} - Tél: ${schoolInfo.phone}</p>
        </div>

        <div class="title">FICHE HEBDOMADAIRE DE COURS</div>
        <div class="title-detail">${viewingFiche.title}</div>
        
        <div class="info-box">
          <p><strong>Professeur:</strong> ${viewingFiche.teacherName}</p>
          <p><strong>Matière:</strong> ${viewingFiche.subjectName}</p>
          <p><strong>Classe:</strong> ${viewingFiche.className}</p>
          <p><strong>Semaine du:</strong> ${viewingFiche.weekStartDate?.toDate().toLocaleDateString('fr-FR')}</p>
          <p><strong>Statut:</strong> ${viewingFiche.status === 'validated' ? 'Validée' : viewingFiche.status === 'rejected' ? 'Rejetée' : 'En attente'}</p>
          ${viewingFiche.validatedBy && viewingFiche.validatedAt ? 
            `<p><strong>Décision prise le:</strong> ${viewingFiche.validatedAt?.toDate().toLocaleDateString('fr-FR')} (Par ${viewingFiche.validatorDisplayName || 'N/A'})</p>` : ''
          }
        </div>

        <div class="content-section">
          ${contentHtml}
        </div>
        
        ${viewingFiche.status === 'rejected' && viewingFiche.rejectionReason ? `
          <div class="content-section" style="border: 1px solid red; background-color: #ffebeb; color: #cc0000;">
            <strong>Raison du Rejet:</strong>
            <div class="content-body">${viewingFiche.rejectionReason}</div>
          </div>
        ` : ''}

        <div class="footer">
          Imprimé le ${new Date().toLocaleDateString('fr-FR')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } else {
      setError("Impossible d'ouvrir la fenêtre d'impression. Veuillez vérifier les bloqueurs de pop-up.");
    }
  };

  const renderFicheForm = () => (
    <div className="p-8 bg-white rounded-xl shadow-2xl border-t-4 border-blue-600">
      <h3 className="text-2xl font-extrabold text-blue-700 mb-6 border-b pb-2">
        {editingFiche ? 'Modifier la Fiche Hebdomadaire' : 'Proposer une Nouvelle Fiche Hebdomadaire'}
      </h3>
      <form onSubmit={handleSubmitFiche} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="weekStartDate" className="block text-sm font-medium text-gray-700">Date de début de semaine</label>
            <input
              type="date"
              id="weekStartDate"
              value={selectedWeekStartDate}
              onChange={(e) => setSelectedWeekStartDate(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Matière</label>
            <select
              id="subject"
              value={selectedSubject}
              onChange={(e) => {setSelectedSubject(e.target.value)}}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Sélectionner une matière</option>
              {subjects.map(subject => (
                <option key={subject.id} value={subject.id}>{subject.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="class" className="block text-sm font-medium text-gray-700">Classe</label>
            <select
              id="class"
              value={selectedClass}
              onChange={(e) => {setSelectedClass(e.target.value)}}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Sélectionner une classe</option>
              {classes.map(_class => (
                <option key={_class.id} value={_class.id}>{_class.nom}</option> 
              ))}
            </select>
          </div>
        </div>
        
        <div>
          <label htmlFor="ficheTitle" className="block text-sm font-medium text-gray-700">Titre de la Fiche</label>
          <input
            type="text"
            id="ficheTitle"
            value={ficheTitle}
            onChange={(e) => setFicheTitle(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ex: Fiche Semaine 1 - Opérations sur les fractions"
            required
          />
        </div>
        
        <div className="space-y-6 p-6 border border-blue-200 rounded-xl bg-blue-50">
          <h4 className="text-xl font-bold text-blue-700 mb-4 border-b pb-1">1. Objectifs Pédagogiques (Audit Clé)</h4>
          
          <SelectWithAdd 
            label="Compétence(s) du Socle Visée(s)"
            options={standardOptions.competences}
            selectedValues={competencesVisees}
            onUpdate={handleUpdateStructuredData}
            optionType="competences" 
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Objectif(s) Spécifique(s) (O1, O2...)</label>
            <textarea
              value={objectifsSpecifiques}
              onChange={(e) => setObjectifsSpecifiques(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 min-h-[100px]"
              placeholder="Ex: O1 : Définir la notion de 'fonction affine'.&#10;O2 : Appliquer les règles de sécurité en laboratoire."
            />
          </div>
        </div>

        <div className="space-y-6 p-6 border border-gray-200 rounded-xl bg-gray-50">
          <h4 className="text-xl font-bold text-gray-700 mb-4 border-b pb-1">2. Démarche et Déroulement (Mise en Œuvre)</h4>
          
          <SelectWithAdd 
            label="Méthode(s) Pédagogique(s) Dominante(s)"
            options={standardOptions.methodes}
            selectedValues={methodesDominantes}
            onUpdate={handleUpdateStructuredData}
            optionType="methodes"
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Déroulement Synthétique des Séances (Jour, Phases, Durées)</label>
            <textarea
              value={deroulementContent}
              onChange={(e) => setDeroulementContent(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-4 min-h-[300px] font-mono text-sm"
              placeholder="Ex: SÉANCE N°1 (Lundi / 55 min) :&#10;- Phase 1 : 15 min - Rappel des prérequis.&#10;- Phase 2 : 30 min - Activité principale (Binôme/Doc B).&#10;- Phase 3 : 10 min - Trace écrite/Synthèse."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Différenciation et Remédiation</label>
            <textarea
              value={differenciationContent}
              onChange={(e) => setDifferenciationContent(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 min-h-[100px]"
              placeholder="Ex: Élèves en difficulté : Feuille d'exercices A, soutien ciblé.&#10;Élèves avancés : Exercices défis, rôle de tuteur."
            />
          </div>
        </div>
        
        <div className="space-y-6 p-6 border border-gray-200 rounded-xl bg-red-50">
          <h4 className="text-xl font-bold text-red-700 mb-4 border-b pb-1">3. Évaluation et Bilan (Traçabilité)</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Évaluation (Formative / Sommative)</label>
            <textarea
              value={evaluationContent}
              onChange={(e) => setEvaluationContent(e.target.value)}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 min-h-[100px]"
              placeholder="Ex: Formative : Observation des productions.&#10;Sommative : Devoir sur table le Vendredi sur les O1 et O2."
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => { clearForm(); setCurrentView('list'); }}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-150"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-150"
          >
            {editingFiche ? 'Mettre à jour la Fiche' : 'Soumettre la Fiche'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderFicheDetails = () => {
    if (!viewingFiche) return null;
    const displayContent = viewingFiche.content || 'Aucun contenu.';
    
    return (
      <div className="p-8 bg-white rounded-xl shadow-2xl border-t-4 border-green-600">
        <div className="flex items-center justify-between mb-6 border-b pb-2">
          <h3 className="text-2xl font-extrabold text-gray-800">
            Détails de la Fiche : {viewingFiche.title}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={handlePrintFiche}
              className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition"
              title="Imprimer la Fiche"
            >
              <Printer size={24} />
            </button>
            <button
              onClick={() => { setViewingFiche(null); setCurrentView('list'); setRejectionReason(''); }}
              className="p-2 text-gray-500 hover:text-gray-700 bg-gray-100 rounded-full transition"
              title="Retour à la liste"
            >
              <XCircle size={24} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p><strong>Professeur:</strong> {viewingFiche.teacherName}</p>
          <p><strong>Matière:</strong> {viewingFiche.subjectName}</p>
          <p><strong>Classe:</strong> {viewingFiche.className}</p>
          <p><strong>Semaine du:</strong> {viewingFiche.weekStartDate?.toDate().toLocaleDateString('fr-FR')}</p>
          <p className="col-span-2"><strong>Statut:</strong> <span className={`font-bold ${viewingFiche.status === 'validated' ? 'text-green-600' : viewingFiche.status === 'rejected' ? 'text-red-600' : 'text-yellow-600'}`}>
            {viewingFiche.status === 'pending' ? 'En attente' : viewingFiche.status === 'validated' ? 'Validée' : 'Rejetée'}
          </span></p>
          {viewingFiche.validatedBy && viewingFiche.validatedAt && (
            <p className="col-span-2"><strong>{viewingFiche.status === 'validated' ? 'Validée' : 'Décision prise'} le:</strong> {viewingFiche.validatedAt?.toDate().toLocaleDateString('fr-FR')} (Par {viewingFiche.validatorDisplayName || 'Chargement...'})</p>
          )}
        </div>

        <div className="border border-gray-300 rounded-md p-6 bg-white shadow-inner mb-6">
          <h4 className="text-xl font-semibold text-gray-800 mb-3 border-b pb-1">Contenu Détaillé:</h4>
          <div className="text-gray-700 whitespace-pre-wrap text-base leading-relaxed">
            {displayContent}
          </div>
        </div>

        {viewingFiche.status === 'rejected' && viewingFiche.rejectionReason && (
          <div className="bg-red-50 border border-red-300 text-red-800 p-4 rounded-lg mb-6">
            <h4 className="font-bold text-lg mb-2 flex items-center"><XCircle size={20} className="mr-2"/> Raison du Rejet:</h4>
            <p className="italic">{viewingFiche.rejectionReason}</p>
          </div>
        )}

        {(userRole === 'directeurPedagogique' || userRole === 'admin' || userRole === 'superAdmin') && viewingFiche.status === 'pending' && (
          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col space-y-4">
            <h4 className="text-lg font-bold text-gray-800">Actions d'Audit</h4>
            <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3">
              <button
                onClick={() => handleValidateFiche(viewingFiche.id)}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 transition duration-150"
              >
                <CheckCircle size={18} className="mr-2" /> Valider la Fiche
              </button>
              <input
                type="text"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Raison du rejet (obligatoire si rejet)"
                className="flex-grow border border-gray-300 rounded-md shadow-sm p-2 focus:ring-red-500 focus:border-red-500"
              />
              <button
                onClick={() => handleRejectFiche(viewingFiche.id)}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition duration-150"
              >
                <XCircle size={18} className="mr-2" /> Rejeter la Fiche
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFichesList = () => {
    return (
      <div className="mb-8">
        {/* Information sur le périmètre d'enseignement */}
        {userRole === 'enseignant' && (teacherClasses.length > 0 || teacherSubjects.length > 0) && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 font-medium">
              📚 <strong>Votre périmètre d'enseignement :</strong>
            </p>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <p className="text-sm text-blue-700">
                  <strong>Classes assignées :</strong> {teacherClasses.length > 0 
                    ? classes.filter(c => teacherClasses.includes(c.id)).map(c => c.nom).join(', ') || 'Aucune classe assignée'
                    : 'Aucune classe assignée'}
                </p>
              </div>
              <div>
                <p className="text-sm text-blue-700">
                  <strong>Matières assignées :</strong> {teacherSubjects.length > 0 
                    ? subjects.filter(s => teacherSubjects.includes(s.id)).map(s => s.name).join(', ') || 'Aucune matière assignée'
                    : 'Aucune matière assignée'}
                </p>
              </div>
            </div>
            {teacherClasses.length > 0 && teacherSubjects.length > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-300">
                <p className="text-xs text-blue-600">
                  <strong>Note :</strong> Vous ne pouvez créer des fiches que pour les combinaisons classe-matière qui vous sont assignées.
                </p>
              </div>
            )}
          </div>
        )}

        {userRole === 'enseignant' && (
          <button
            onClick={() => {clearForm(); setCurrentView('create');}}
            className="mb-8 px-6 py-3 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition duration-200 flex items-center font-semibold"
          >
            <PlusCircle size={20} className="mr-2" /> Proposer une Nouvelle Fiche
          </button>
        )}

        {!schoolId && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 mb-6">
            <p className="font-semibold">⚠️ Veuillez sélectionner une école pour gérer les fiches de cours.</p>
          </div>
        )}

        {schoolId && (
          <>
            <h3 className="text-2xl font-bold text-yellow-700 mb-4 border-b-2 border-yellow-300 pb-1">⏳ En Attente de Validation ({pendingFiches.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
              {pendingFiches.length > 0 ? (
                pendingFiches.map(fiche => (
                  <div key={fiche.id} className="bg-yellow-50 border border-yellow-200 rounded-lg shadow-md p-5 flex flex-col justify-between h-full">
                    <div>
                      <p className="text-gray-900 font-bold text-lg mb-2">{fiche.title}</p>
                      <p className="text-sm text-gray-600 mb-1">Prof: {fiche.teacherName}</p>
                      <p className="text-sm text-gray-600 mb-1">Matière: {fiche.subjectName}</p>
                      <p className="text-sm text-gray-600 mb-3">Semaine du: <span className="font-semibold">{fiche.weekStartDate?.toDate().toLocaleDateString('fr-FR')}</span></p>
                    </div>
                    <div className="flex mt-3 space-x-2 border-t pt-3">
                      <button onClick={() => handleViewFiche(fiche)} className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200" title="Voir les détails"><Eye size={18} /></button>
                      {userRole === 'enseignant' && fiche.teacherAuthUid === userId && (
                        <>
                          <button onClick={() => handleEditFiche(fiche)} className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200" title="Modifier"><Edit size={18} /></button>
                          <button onClick={() => handleDeleteFiche(fiche.id)} className="p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200" title="Supprimer"><Trash2 size={18} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 italic col-span-full p-4 bg-white rounded-lg border">Aucune fiche en attente de validation.</p>
              )}
            </div>

            <h3 className="text-2xl font-bold text-green-700 mb-4 border-b-2 border-green-300 pb-1">✅ Validées ({validatedFiches.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
              {validatedFiches.length > 0 ? (
                validatedFiches.map(fiche => (
                  <div key={fiche.id} className="bg-green-50 border border-green-200 rounded-lg shadow-md p-5 flex flex-col justify-between h-full">
                    <div>
                      <p className="text-gray-900 font-bold text-lg mb-2">{fiche.title}</p>
                      <p className="text-sm text-gray-600 mb-1">Prof: {fiche.teacherName}</p>
                      <p className="text-sm text-gray-600 mb-1">Matière: {fiche.subjectName}</p>
                      <p className="text-sm text-gray-600 mb-3">Semaine du: <span className="font-semibold">{fiche.weekStartDate?.toDate().toLocaleDateString('fr-FR')}</span></p>
                    </div>
                    <div className="flex mt-3 space-x-2 border-t pt-3">
                      <button onClick={() => handleViewFiche(fiche)} className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200" title="Voir les détails"><Eye size={18} /></button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 italic col-span-full p-4 bg-white rounded-lg border">Aucune fiche validée.</p>
              )}
            </div>

            <h3 className="text-2xl font-bold text-red-700 mb-4 border-b-2 border-red-300 pb-1">❌ Rejetées ({rejectedFiches.length})</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {rejectedFiches.length > 0 ? (
                rejectedFiches.map(fiche => (
                  <div key={fiche.id} className="bg-red-50 border border-red-200 rounded-lg shadow-md p-5 flex flex-col justify-between h-full">
                    <div>
                      <p className="text-gray-900 font-bold text-lg mb-2">{fiche.title}</p>
                      <p className="text-sm text-gray-600 mb-1">Prof: {fiche.teacherName}</p>
                      <p className="text-sm text-gray-600 mb-1">Raison: <span className="text-red-700 font-medium">{fiche.rejectionReason || 'Non spécifiée'}</span></p>
                      <p className="text-sm text-gray-600 mb-3">Semaine du: <span className="font-semibold">{fiche.weekStartDate?.toDate().toLocaleDateString('fr-FR')}</span></p>
                    </div>
                    <div className="flex mt-3 space-x-2 border-t pt-3">
                      <button onClick={() => handleViewFiche(fiche)} className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200" title="Voir les détails"><Eye size={18} /></button>
                      {userRole === 'enseignant' && fiche.teacherAuthUid === userId && (
                        <>
                          <button onClick={() => handleEditFiche(fiche)} className="p-2 bg-green-100 text-green-700 rounded-full hover:bg-green-200" title="Modifier/Resoumettre"><Edit size={18} /></button>
                          <button onClick={() => handleDeleteFiche(fiche.id)} className="p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200" title="Supprimer"><Trash2 size={18} /></button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 italic col-span-full p-4 bg-white rounded-lg border">Aucune fiche rejetée.</p>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-md p-6">
        <Loader2 className="animate-spin text-blue-600" size={48} />
        <p className="ml-3 text-lg text-gray-700">Chargement des fiches de cours...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 rounded-xl shadow-inner min-h-[80vh]">
      <h2 className="text-4xl font-extrabold text-gray-900 mb-8 flex items-center border-b pb-2">
        <CalendarDays size={32} className="mr-3 text-blue-600" /> Gestion des Fiches Hebdomadaires de Cours
      </h2>

      {currentView === 'list' && renderFichesList()}
      {currentView === 'create' && renderFicheForm()}
      {currentView === 'view' && renderFicheDetails()}
    </div>
  );
};

export default CoursManagement;